import * as EmailSchedule from './EmailSchedule';
import { VPResult } from '../VPResult';
import * as VPErrors from '../../VPError';
import { ApolloClient, gql } from '@apollo/client/core';
import { Mutation, Query } from '../../generated/api.graphql';
import { VP_ERROR_FIELDS } from '../VPError/fragments';

const EMAIL_SCHEDULE_FIELDS = gql`
  fragment EmailScheduleFields on EmailSchedule {
    id
    eventId
    emailType
    hour
    minute
    daysOfWeek
    startDate
    endDate
    to
    cc
    bcc
    lastSentAt
  }
`;

export const EMAIL_SCHEDULE_QUERY = gql`
  query EmailSchedule($eventId: Int!) {
    emailSchedule(eventId: $eventId) {
      ...EmailScheduleFields
    }
  }
  ${EMAIL_SCHEDULE_FIELDS}
`;

export const SAVE_EMAIL_SCHEDULE_MUTATION = gql`
  mutation SaveEmailSchedule($input: SaveEmailScheduleMutationInput!) {
    saveEmailSchedule(input: $input) {
      emailSchedule {
        ...EmailScheduleFields
      }
      errors { ...VPErrorFields }
    }
  }
  ${EMAIL_SCHEDULE_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_EMAIL_SCHEDULE_MUTATION = gql`
  mutation DeleteEmailSchedule($input: DeleteEmailScheduleMutationInput!) {
    deleteEmailSchedule(input: $input) {
      success
      errors { ...VPErrorFields }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const RESEND_EMAIL_SCHEDULE_MUTATION = gql`
  mutation ResendEmailSchedule($input: ResendEmailScheduleMutationInput!) {
    resendEmailSchedule(input: $input) {
      success
      errors { ...VPErrorFields }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const REPORT_SHARE_TOKEN_QUERY = gql`
  query EventReportShareToken($eventId: Int!) {
    event(id: $eventId) {
      reportShareToken
    }
  }
`;

export const REGENERATE_SHAREABLE_REPORT_LINK_MUTATION = gql`
  mutation RegenerateShareableReportLink($input: RegenerateShareableReportLinkMutationInput!) {
    regenerateShareableReportLink(input: $input) {
      event {
        reportShareToken
      }
      errors { ...VPErrorFields }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const fetchSchedule = async (
  client: ApolloClient<Query>,
  eventId: number,
): Promise<EmailSchedule.EmailSchedule | null> => {
  const { data } = await client.query({
    query: EMAIL_SCHEDULE_QUERY,
    variables: { eventId },
    fetchPolicy: 'network-only',
  });

  return data?.emailSchedule ? EmailSchedule.fromGql(data.emailSchedule) : null;
};

export const fetchSchedules = async (
  client: ApolloClient<Query>,
  eventId: number,
): Promise<EmailSchedule.EmailSchedule[]> => {
  const schedule = await fetchSchedule(client, eventId);
  return schedule ? [schedule] : [];
};

export const saveSchedule = async (
  client: ApolloClient<Mutation>,
  params: EmailSchedule.EmailScheduleAttributes,
): Promise<VPResult<EmailSchedule.EmailSchedule>> => {
  const { data } = await client.mutate({
    mutation: SAVE_EMAIL_SCHEDULE_MUTATION,
    variables: { input: { emailSchedule: EmailSchedule.toGqlInput(params) } },
  });

  const response = data?.saveEmailSchedule;

  if (!response?.emailSchedule) {
    return { success: false, errors: VPErrors.fromGqlCollection(response?.errors) };
  }
  return { success: true, data: EmailSchedule.fromGql(response.emailSchedule) };
};

export const deleteSchedule = async (
  client: ApolloClient<Mutation>,
  emailScheduleId: number,
): Promise<VPResult<void>> => {
  const { data } = await client.mutate({
    mutation: DELETE_EMAIL_SCHEDULE_MUTATION,
    variables: { input: { emailScheduleId } },
  });

  const response = data?.deleteEmailSchedule;

  if (!response?.success) {
    return { success: false, errors: VPErrors.fromGqlCollection(response?.errors) };
  }
  return { success: true, data: undefined };
};

type ResendEmailScheduleResponse = {
  resendEmailSchedule?: {
    success: boolean;
    errors?: Parameters<typeof VPErrors.fromGqlCollection>[0];
  };
};

export const resendSchedule = async (
  client: ApolloClient<Mutation>,
  emailScheduleId: number,
  recipientEmails: string[],
): Promise<VPResult<void>> => {
  const { data } = await client.mutate<ResendEmailScheduleResponse>({
    mutation: RESEND_EMAIL_SCHEDULE_MUTATION,
    variables: { input: { emailScheduleId, recipientEmails } },
  });

  const response = data?.resendEmailSchedule;

  if (!response?.success) {
    return { success: false, errors: VPErrors.fromGqlCollection(response?.errors) };
  }
  return { success: true, data: undefined };
};

type ReportShareTokenQueryResponse = {
  event?: {
    reportShareToken?: string | null;
  } | null;
};

export const fetchReportShareToken = async (
  client: ApolloClient<Query>,
  eventId: number,
): Promise<string | null> => {
  const { data } = await client.query<ReportShareTokenQueryResponse>({
    query: REPORT_SHARE_TOKEN_QUERY,
    variables: { eventId },
    fetchPolicy: 'network-only',
  });

  return data?.event?.reportShareToken ?? null;
};

type RegenerateShareableReportLinkResponse = {
  regenerateShareableReportLink?: {
    event?: {
      reportShareToken?: string | null;
    } | null;
    errors?: Parameters<typeof VPErrors.fromGqlCollection>[0];
  };
};

export const regenerateShareableReportLink = async (
  client: ApolloClient<Mutation>,
  eventId: number,
): Promise<VPResult<string>> => {
  const { data } = await client.mutate<RegenerateShareableReportLinkResponse>({
    mutation: REGENERATE_SHAREABLE_REPORT_LINK_MUTATION,
    variables: { input: { eventId } },
  });

  const response = data?.regenerateShareableReportLink;
  const token = response?.event?.reportShareToken;

  if (!token) {
    return { success: false, errors: VPErrors.fromGqlCollection(response?.errors) };
  }
  return { success: true, data: token };
};
